import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Modal from "@/features/shared/components/Modal/Modal";
import useTasks from "@/features/task/hooks/useTasks";
import TaskTableFilters from "@/features/task/components/TaskTableFilters";
import TaskTable from "@/features/task/components/TaskTable";
import TaskPagination from "@/features/task/components/TaskPagination";
import { FiltersSection } from "@/features/task/components/FiltersSection";
import TaskForm from "@/features/task/components/taskForm/TaskForm";
import TaskStats from "@/features/task/components/TaskStats/TaskStats";
import useTaskStates from "@/features/taskState/hooks/useTaskStates";
import ConfirmDeleteTaskModal from "@/features/task/components/ConfirmDeleteTaskModal";
import { toast } from "react-toastify";
import { BarChart2 } from "lucide-react";
import { useAuth } from "@/auth/useAuth";
import TaskTrashPage from "@/features/taskTrash/pages/TaskTrashPage";
import { buildDefaultTaskFilters, getRestrictedAssigneeId } from "@/features/task/utils/filterUtils";
import { FiTrash2 } from "react-icons/fi";
const TaskListPage = () => {
    const { user } = useAuth();
    const hasTrashAccess = Boolean(user?.permissions?.includes("delete_task"));
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = hasTrashAccess && searchParams.get("tab") === "trash" ? "trash" : "tasks";
    const restrictedAssigneeId = useMemo(() => getRestrictedAssigneeId(user), [user]);
    const isRestrictedUser = Boolean(restrictedAssigneeId);
    const [filters, setFilters] = useState(() => buildDefaultTaskFilters(restrictedAssigneeId));
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [showStats, setShowStats] = useState(true);
    const { taskStates } = useTaskStates(true);
    useEffect(() => {
        if (!restrictedAssigneeId) {
            return;
        }
        setFilters(prev => {
            const currentAssignees = Array.isArray(prev.assignees) ? prev.assignees : [];
            if (currentAssignees.length === 1 && currentAssignees[0] === restrictedAssigneeId) {
                return prev;
            }
            return {
                ...prev,
                assignees: [restrictedAssigneeId],
            };
        });
    }, [restrictedAssigneeId]);
    // Pass currentPage and filters to useTasks, so it refetches when they change.
    const { tasks, meta, loading, error, handleAddTask, handleUpdateTask, handleDeleteTask, handleChangeTaskState, refetchTasks } = useTasks(currentPage, filters, false, rowsPerPage);
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => {
            if (filterName === "assignees") {
                if (isRestrictedUser && restrictedAssigneeId) {
                    return {
                        ...prev,
                        assignees: [restrictedAssigneeId],
                    };
                }
                return {
                    ...prev,
                    assignees: Array.isArray(value) ? value : [],
                };
            }
            const nextFilters = {
                ...prev,
                [filterName]: value,
            };
            if (isRestrictedUser && restrictedAssigneeId) {
                nextFilters.assignees = [restrictedAssigneeId];
            }
            return nextFilters;
        });
        setCurrentPage(1); // Reset to first page on filter change
    };
    const navigate = useNavigate();
    const handleEdit = (task) => {
        if (window.innerWidth < 768) {
            navigate(`/tasks/${task.id}/edit`);
        }
        else {
            setSelectedTask(task);
            setIsModalVisible(true);
        }
    };
    const handleDeleteClick = (id) => {
        setTaskToDelete(id);
        setIsDeleteModalVisible(true);
    };
    const confirmDelete = async () => {
        if (taskToDelete === null)
            return;
        try {
            await handleDeleteTask(taskToDelete);
            toast.success("Tâche supprimé");
        }
        catch (error) {
            toast.error(`Erreur : ${error.message}`);
        }
        finally {
            setIsDeleteModalVisible(false);
            setTaskToDelete(null);
        }
    };
    const cancelDelete = () => {
        setIsDeleteModalVisible(false);
        setTaskToDelete(null);
    };
    const handleSubmit = async (taskData) => {
        if (selectedTask && selectedTask.id) {
            await handleUpdateTask(selectedTask.id, taskData);
        }
        else {
            await handleAddTask(taskData);
        }
        setIsModalVisible(false);
    };
    const handleCloseModal = () => {
        setSelectedTask(null);
        setIsModalVisible(false);
    };
    const renderTasksTab = () => (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex items-center gap-4 mb-2", children: _jsxs("button", { onClick: () => setShowStats(show => !show), title: `${showStats ? "Masquer les stats" : "Afficher les stats"}`, "aria-label": `${showStats ? "Masquer les stats" : "Afficher les stats"}`, className: "bg-green-400 text-white px-2 py-1 rounded hover:bg-green-500", children: [_jsx(BarChart2, { className: "w-5 h-5", "aria-hidden": "true" }), _jsx("span", { className: "sr-only", children: showStats ? "Masquer les stats" : "Afficher les stats" })] }) }), showStats && (_jsx(TaskStats, { selectedStatus: filters.status, onStatusFilterChange: (status) => handleFilterChange('status', status) })), _jsx(FiltersSection, { table: _jsxs(_Fragment, { children: [loading && _jsx("p", { children: "Chargement..." }), error && (_jsxs("p", { className: "text-red-500", children: ["Erreur lors du chargement des t\u00E2ches : ", error, _jsx("button", { onClick: () => refetchTasks(), className: "ml-4 bg-blue-500 text-white px-2 py-1 rounded", children: "R\u00E9essayer" })] })), _jsx("div", { className: "overflow-x-auto", children: _jsx(TaskTable, { tasks: tasks, onEdit: handleEdit, onDelete: handleDeleteClick, onChangeState: handleChangeTaskState, taskStates: taskStates }) }), meta && (_jsx(TaskPagination, { currentPage: meta.page ?? 1, totalPages: meta.totalPages ?? 1, limit: rowsPerPage, onPageChange: (page) => setCurrentPage(page), onLimitChange: (value) => {
                                setRowsPerPage(value);
                                setCurrentPage(1);
                            } }))] }), children: _jsx(TaskTableFilters, { filters: filters, isKanban: false, onFilterChange: handleFilterChange, onResetFilters: () => {
                        setFilters(buildDefaultTaskFilters(restrictedAssigneeId));
                        setCurrentPage(1);
                    } }) }), isModalVisible && (_jsx(Modal, { onClose: handleCloseModal, children: _jsx(TaskForm, { taskId: selectedTask?.id, onSubmit: handleSubmit, onCancel: handleCloseModal }) })), isDeleteModalVisible && (_jsx(ConfirmDeleteTaskModal, { onConfirm: confirmDelete, onCancel: cancelDelete }))] }));
    return (_jsxs("div", { className: "p-4 bg-gray-50 min-h-screen", children: [_jsxs("div", { className: "mb-4 flex flex-wrap gap-2 border-b border-gray-200", children: [_jsx("button", { type: "button", onClick: () => setSearchParams({}), className: `rounded-t px-4 py-2 text-sm font-medium ${activeTab === "tasks" ? "bg-white text-blue-600 shadow" : "text-gray-600 hover:text-blue-600"}`, children: "T\u00E2ches" }), hasTrashAccess && (_jsx("button", { type: "button", onClick: () => setSearchParams({ tab: "trash" }), title: "Corbeille", className: `rounded-t px-4 py-2 text-sm font-medium ${activeTab === "trash" ? "bg-white text-blue-600 shadow" : "text-gray-600 hover:text-blue-600"}`, children: _jsx(FiTrash2, {}) }))] }), activeTab === "tasks" && renderTasksTab(), activeTab === "trash" && hasTrashAccess && _jsx(TaskTrashPage, {})] }));
};
export default TaskListPage;
